import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { fromSdkTableList, TableAdapter } from '@makyoapp/makyo-common'

import { DBClient, DEFAULT_LANGUAGE, DEFAULT_USER_SPECIFIC, IDBTable } from '@/core/db'
import { IDBProvider, StandardDBProvider } from '@/core/db/provider'
import { TanstackQueryProvider } from '@/core/db/provider/tanstack-query'
import { getActiveLanguage } from '@/core/db/utils'
import { useDbWorker } from '@/workers/useDbWorker'
import constate from '@/lib/constate'
import { getLanguage, Language } from '@/lib/language-flag'
import { queryClient, queryKeys } from '@/lib/react-query'
import { DbItemAdapterV2, fromSdkDb } from '@/adapters/DbAdapter'
import { EVENT_KEYS } from '@/shared/custom-event'

const lastWorkerFetchMap = new Map<string, number>()

const DEFAULT_LANGUAGES = !!getLanguage('en') ? [getLanguage('en')!] : []

interface DbClientProviderProps {
  databaseID: string
  appID?: string
}

const dbClientProvider = (window.appMode === 'preview' ? TanstackQueryProvider : StandardDBProvider) as IDBProvider

const [DbClientProvider, useDbClientContext] = constate(
  'DbClientContext',
  ({ databaseID, appID }: DbClientProviderProps) => {
    const dbClientRef = useRef<DBClient | undefined>()
    const dbClient = dbClientRef.current

    // Initialize dbWorker
    const {
      worker: dbWorker,
      status: workerStatus,
      initDbWorker,
      closeDbWorker,
      updateDbClient,
      loadedTables,
      tablesLoadingMap,
      wasmProcessing,
      snapshotLoadingSet,
      setSnapshotLoadingSet,
    } = useDbWorker()

    // Core State
    const [loadingDbClient, setLoadingDbClient] = useState(true)
    const [db, setDb] = useState<DbItemAdapterV2 | undefined>()
    const [tables, setTables] = useState<TableAdapter[]>([])
    const [tablesMap, setTablesMap] = useState<Map<string, IDBTable>>(new Map())

    // Refs
    const unsubscribeRef = useRef<() => void | undefined>()

    const [language, setLanguage] = useState(DEFAULT_LANGUAGE)
    const [userSpecific, setUserSpecific] = useState(DEFAULT_USER_SPECIFIC)

    const defaultLanguage = useMemo(() => {
      if (!db || !db.defaultLanguage) return 'en'
      return db.defaultLanguage
    }, [db])

    const languages = useMemo(() => {
      if (!db || !db.languages.length) return DEFAULT_LANGUAGES
      return db.languages.map(getLanguage).filter((l) => !!l)
    }, [db])

    const updateLanguages = useCallback(
      async (langs: Language[], defaultLanguage?: string) => {
        await dbClient?.UpdateLanguages(
          langs.map((l) => l.ids.locale),
          defaultLanguage
        )
      },
      [dbClient]
    )

    // default user table
    const userTable = useMemo(() => {
      return tables.find((tb) => tb.isUserTable)
    }, [tables])

    const sessionTable = useMemo(() => {
      return tables.find((tb) => tb.isSessionTable)
    }, [tables])

    const [showCreateTableOptions, setShowCreateTableOptions] = useState(false)
    const [showCreateTable, setShowCreateTable] = useState(false)
    const [showQueryTable, setShowQueryTable] = useState<'create' | 'edit' | null>(null)
    const [showSnapshotTable, setShowSnapshotTable] = useState<'create' | 'edit' | null>(null)

    const handleSetDbState = useCallback(
      (dbClient: DBClient) => {
        dbClientRef.current = dbClient
        updateDbClient(dbClient)
        setLanguage(getActiveLanguage(appID) || dbClient.settings?._DefaultLanguage || DEFAULT_LANGUAGE)
        setTablesMap(new Map(dbClient.tablesMap))
        setTables(fromSdkTableList(dbClient.GetTables().map((tb) => tb.settings)))
      
        if (dbClient.settings) {
          setDb(fromSdkDb(dbClient.settings))
        }
      },
      [appID, updateDbClient]
    )

    // ========================== Effects =============================

    /**
     * terminate current dbClient instance,
     * release memory, reset all states
     */
    const closeDbClient = useCallback(() => {
      window.prettyLog(':--CLOSING DB CLIENT--', '#ab2a19')

      // clear all table data queries
      queryClient.removeQueries({
        predicate: (query) => {
          return [queryKeys.gridTableData, queryKeys.compTableData].includes(query.queryKey[0] as any)
        },
      })

      if (dbClientRef.current) {
        dbClientRef.current.Close()
        dbClientRef.current = undefined
      }
      if (unsubscribeRef.current) {
        unsubscribeRef.current()
        unsubscribeRef.current = undefined
      }

      lastWorkerFetchMap.clear()
      closeDbWorker()
      setDb(undefined)
      setTables([])
      setTablesMap(new Map())
    }, [closeDbWorker])

    /**
     * Main function to initialize dbClient
     */
    const openDbClient = useCallback(
      async (databaseID: string) => {
        window.prettyLog(':--OPENING DB CLIENT--', '#107d19')

        setLoadingDbClient(true)

        if (!!dbClientRef.current) {
          closeDbClient()
        }

        try {
          // open database
          const dbClient = await DBClient.Open(databaseID, dbClientProvider)

          // init dbWorker
          initDbWorker(dbClient)

          // emit `db-opened` custom event
          window.dispatchEvent(new CustomEvent(EVENT_KEYS.DB_OPENED))

          // set dbClient state
          handleSetDbState(dbClient)

          unsubscribeRef.current = dbClient.subscribe(() => {
            handleSetDbState(dbClient)
          })
          //
        } catch (err) {
          console.error('[DBResolver] Error opening database:', err)
        } finally {
          setLoadingDbClient(false)
        }
      },
      [closeDbClient, handleSetDbState, initDbWorker]
    )

    const handlePageReload = useCallback(
      (event: PageTransitionEvent) => {
        if (event.persisted) return

        const navEntries = performance.getEntriesByType('navigation')
        if (
          navEntries.length > 0 &&
          navEntries[0] &&
          (navEntries[0] as PerformanceNavigationTiming).type === 'reload'
        ) {
          closeDbClient()
        }
      },
      [closeDbClient]
    )

    // Initial database load when dbId changes
    useEffect(() => {
      if (!databaseID) return

      // open dbClient
      openDbClient(databaseID)

      // listen to force-reload event
      const handleDbClientReload = () => openDbClient(databaseID)
      window.addEventListener('db-client-reload', handleDbClientReload)

      // Manually trigger cleanup on browser refresh
      window.addEventListener('pagehide', handlePageReload)

      // Cleanup
      return () => {
        // close dbClient
        closeDbClient()
        // remove event listener
        window.removeEventListener('db-client-reload', handleDbClientReload)
        window.removeEventListener('pagehide', handlePageReload)
      }
      //
    }, [databaseID, closeDbClient, openDbClient, handlePageReload])

    // ========================= Table Methods =========================

    return {
      dbClient: dbClientRef.current,
      db,
      dbId: databaseID,
      dbWorker,
      dbWorkerStatus: workerStatus,
      loadingDbClient: loadingDbClient || workerStatus !== 'READY',
      lastWorkerFetchMap,
      loadedTables,
      tablesLoadingMap,
      wasmProcessing,
      snapshotLoadingSet,
      setSnapshotLoadingSet,

      // Language
      language,
      setLanguage,
      defaultLanguage,
      languages,
      updateLanguages,

      userSpecific,
      setUserSpecific,

      tablesMap,
      tables,
      setTables,
      userTable,
      sessionTable,
      showCreateTable,
      setShowCreateTable,
      showQueryTable,
      setShowQueryTable,
      showSnapshotTable,
      setShowSnapshotTable,
      showCreateTableOptions,
      setShowCreateTableOptions,
      setDb,
    }
  }
)

export { DbClientProvider, useDbClientContext }
